<template>
  <MDBNavbarItem class="menu-item-border p-relative">
    <a
      href="#"
      class="nav-link px-lg-3 px-1 px-sm-2 py-0"
      :class="{ 'cart-preview-trigger--disabled': cartPreviewBusy }"
      @click.prevent="toggleCartPreview"
    >
      <img :src="require('@/assets/img/icons/cart.svg')" alt="cart" />
      <MDBBadge v-if="cartBadgeCount > 0" color="danger" pill notification>
        {{ cartBadgeCount }}
      </MDBBadge>
    </a>
    <div
      v-if="cartPreviewOpen"
      class="cart-preview-backdrop"
      :class="{ 'cart-preview-trigger--disabled': cartPreviewBusy }"
      @click="closeCartPreview"
    ></div>
    <div v-if="cartPreviewOpen" class="cart-preview-wrap">
      <h5 class="fw-bold mb-3">{{ $t("shop.shoppingCart") }}</h5>
      <div v-if="cartLoading" class="cart-preview-loader">
        <Loader :show="true" spinner="spinner-red.svg" />
      </div>
      <template v-else>
        <ul v-if="cart?.items?.length > 0" class="cart-preview-list mb-3">
          <li v-for="(item, i) in cart.items" :key="`cart_preview_${i}`" class="cart-preview-item">
            <span class="cart-preview-item__name">{{ getProductName(item.product) }}</span>
            <span class="cart-preview-item__qty text-muted">
              {{ item.quantity }} × {{ showPoints(item) }}
            </span>
          </li>
        </ul>
        <p v-else class="text-muted mb-3">{{ $t("shop.emptyCart") }}</p>
        <div class="cart-preview-points mb-3">
          <p class="cart-preview-points__row">
            <span>{{ $t("points.dashboard.dashboardBox.availPoints") }}</span>
            <strong>{{ $filters.formatNumberWithCommas(availPoints) }}</strong>
          </p>
          <template v-if="cart?.items?.length > 0">
            <p class="cart-preview-points__row">
              <span>{{ $t("shop.pointsTotal") }}</span>
              <strong>{{ $filters.formatNumberWithCommas(cartTotal) }}</strong>
            </p>
            <p class="cart-preview-points__row mb-0">
              <span>{{ $t("shop.cartRemainPoints") }}</span>
              <strong>{{ $filters.formatNumberWithCommas(remainPoints) }}</strong>
            </p>
          </template>
        </div>
        <MDBBtn color="primary" size="sm" class="w-100" @click="goToCart">
          {{ $t("shop.shoppingCart") }}
        </MDBBtn>
      </template>
    </div>
  </MDBNavbarItem>
</template>

<script setup>
import { MDBNavbarItem, MDBBadge, MDBBtn } from "mdb-vue-ui-kit";
import Loader from "@/components/General/Loader/index.vue";
import { useRouter } from "vue-router";
import { computed, ref, watch, nextTick } from "vue";
import { useStore } from "vuex";
import TokenService from "@/services/storage.service";
import { useItemsConditions } from "@/composables/useItemsConditions";

const store = useStore();
const router = useRouter();
const storedLocale = computed(() => store.getters.getCurrentLocale);
const isLite = computed(() => store.getters.isLiteVersion);

const { getContext } = useItemsConditions();
const context = computed(() => getContext());

const cart = computed(() => store.getters.getCart);
const me = computed(() => store.getters.getMe);
const cartLoaded = computed(() => Array.isArray(cart.value?.items));
const cartBadgeCount = computed(() => {
  if (cartLoaded.value) {
    return itemsAmount(cart.value.items) || 0;
  }

  return me.value?.current_cart_products_count || 0;
});
const availPoints = computed(() => me.value?.points?.avail_points ?? 0);
const cartTotal = computed(() => cart.value?.total ?? 0);
const remainPoints = computed(() => availPoints.value - cartTotal.value);

const cartPreviewOpen = ref(false);
const cartLoading = ref(false);
const cartPreviewBusy = ref(false);
const lastCartPreviewFetchAt = ref(0);
const CART_PREVIEW_FETCH_COOLDOWN_MS = 2500;

let cartPreviewFetchInFlight = null;

const itemsAmount = (data) => {
  if (data?.length > 0) {
    return data.reduce((accumulator, object) => accumulator + object.quantity, 0);
  }
};

const getProductName = (product) => {
  if (!product) return "";
  const localizedName = product?.name?.[storedLocale.value];
  if (localizedName) return localizedName;
  const fallbackName =
    product?.name && typeof product.name === "object"
      ? Object.values(product.name).find((value) => value)
      : product?.name;
  return fallbackName || product?.sku || product?.id || "";
};

const showPoints = (item) => {
  return item.product_variant ? item.product_variant.points : item.product?.points;
};

const closeCartPreview = () => {
  if (cartPreviewBusy.value) {
    return;
  }

  cartPreviewOpen.value = false;
};

const shouldFetchCartPreview = () => {
  if (!lastCartPreviewFetchAt.value) {
    return true;
  }

  return Date.now() - lastCartPreviewFetchAt.value > CART_PREVIEW_FETCH_COOLDOWN_MS;
};

const ensureUserLoaded = async () => {
  if (me.value?.plainId || TokenService.getUserId()) {
    return;
  }

  if (!TokenService.getToken()) {
    return;
  }

  if (store.getters.getIsMePending) {
    await new Promise((resolve) => {
      const stop = watch(
        () => store.getters.getMe?.plainId,
        (plainId) => {
          if (plainId) {
            stop();
            resolve();
          }
        },
      );
    });
    return;
  }

  await store.dispatch("me");
};

const loadCartPreview = async () => {
  if (cartPreviewFetchInFlight) {
    return cartPreviewFetchInFlight;
  }

  cartPreviewFetchInFlight = (async () => {
    await ensureUserLoaded();
    return store.dispatch("fetchUserShopCart");
  })();

  try {
    return await cartPreviewFetchInFlight;
  } finally {
    cartPreviewFetchInFlight = null;
  }
};

const toggleCartPreview = async () => {
  if (cartPreviewBusy.value) {
    return;
  }

  if (cartPreviewOpen.value) {
    closeCartPreview();
    return;
  }

  cartPreviewOpen.value = true;

  const needsFetch = shouldFetchCartPreview();
  cartLoading.value = needsFetch;

  if (!needsFetch) {
    return;
  }

  cartPreviewBusy.value = true;

  await nextTick();

  try {
    await loadCartPreview();
    lastCartPreviewFetchAt.value = Date.now();
  } finally {
    cartLoading.value = false;
    cartPreviewBusy.value = false;
  }
};

const goToCart = () => {
  cartPreviewOpen.value = false;
  router.push({ name: "shoppingCart" });
};

watch(
  () => [me.value?.plainId, me.value?.current_cart_products_count],
  async ([plainId, cartCount]) => {
    if (!plainId || isLite.value || !context.value.canSeeCartIcon) {
      return;
    }

    const hasCartItems = (cart.value?.items?.length ?? 0) > 0;
    if (cartCount > 0 && !hasCartItems && shouldFetchCartPreview() && !cartPreviewBusy.value) {
      cartPreviewBusy.value = true;
      try {
        await loadCartPreview();
        lastCartPreviewFetchAt.value = Date.now();
      } finally {
        cartPreviewBusy.value = false;
      }
    }
  },
);
</script>

<style scoped lang="scss">
@import "@/styles/cart-preview.scss";
</style>
